/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback, useLayoutEffect, useContext, useRef } from "react";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { ROUTES } from "../../../../constant";
import useMediaQuery from "@mui/material/useMediaQuery";
import { isPortraitLockActive } from "../../../../utils/portraitLock";

export const useGlobalEvents = () => {
  const { setIsMobile, isOpenMobileSidebar, isMobile, setIosNativeLoginData }: iGlobalContext =
    useContext(StateContext);

  const localIsMobile = useMediaQuery("(max-width:740px)");
  const pathname = window.location.pathname; // or useLocation if calling inside Router context, which it is.

  const checkMobileView = useCallback(() => {
    // Skip the isMobile flip while the portrait-lock overlay (App.scss) is covering the
    // app. Large phones in landscape exceed the 740px breakpoint (iPhone Pro Max 932,
    // Pixel Pro 992), so a width-based check incorrectly classifies a rotated phone as
    // desktop. Updating isMobile under the overlay cascades into the mobile↔desktop
    // navigation bridges in App.tsx / BookingInProgress.tsx and into route-level screen
    // resets (e.g. flightDelay, ViewShareItinerary), wiping the user's route + screen
    // state by the time they rotate back. With this guard isMobile is frozen for the
    // duration of the overlay; when the user rotates back to portrait resize fires
    // again, the guard releases, and the value is recomputed from the actual width.
    if (isPortraitLockActive()) return;
    if (window.matchMedia("(max-width: 740px)").matches) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [setIsMobile]);

  const handleMobileUpdate = useCallback(() => {
    // Same rationale as checkMobileView above — even though this only sets `true`, we
    // skip during the overlay so the resize batch is fully idempotent from the user's
    // perspective. Avoids spurious context-state writes on every rotation tremor.
    if (isPortraitLockActive()) return;
    setIsMobile(true);
  }, [setIsMobile]);

  useLayoutEffect(() => {
    const isHomeOrTopDestination =
      pathname === ROUTES.Home ||
      pathname === ROUTES.TopDestination ||
      pathname === ROUTES.Faqs ||
      pathname === ROUTES.AboutUs ||
      pathname === ROUTES.PaymentSuccess ||
      pathname === ROUTES.ModifyHotelsSuccess ||
      ROUTES.PaymentFailure;
    const isResetPasswordRoute = pathname.startsWith("/resetpassword/");
    const updateFunction = isHomeOrTopDestination || isResetPasswordRoute ? checkMobileView : handleMobileUpdate;

    window.addEventListener("resize", updateFunction);
    updateFunction();

    return () => window.removeEventListener("resize", updateFunction);
  }, [checkMobileView, handleMobileUpdate, pathname]);

  useEffect(() => {
    if (isOpenMobileSidebar && isMobile) {
      document.documentElement.style.overflowY = "hidden";
    } else {
      document.documentElement.style.overflowY = "auto";
    }
    return () => {
      document.documentElement.style.overflowY = "auto";
    };
  }, [isMobile, isOpenMobileSidebar]);

  const lastTabLeaveTimeRef: any = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // User is leaving the tab, record the time
        lastTabLeaveTimeRef.current = Date.now();
      } else if (document.visibilityState === "visible") {
        // User has returned to the tab
        const now = Date.now();
        const timeAway = now - (lastTabLeaveTimeRef.current || now);

        // Check if user was away for 15 minutes (900,000 ms)
        if (timeAway >= 900000 && localIsMobile) {
          window.location.reload();
        }

        // Reset the last leave time
        lastTabLeaveTimeRef.current = null;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Flutter bridge

  (window as any).fromFlutter = function (data: any) {
    const params = data;
    if (typeof params === "string") setIosNativeLoginData(JSON.parse(data));
    else setIosNativeLoginData(data);
  };

  return { localIsMobile };
};
